import { ActionType } from 'typesafe-actions';

import { IDictionaryValues } from 'APIPackage/DictionariesModule/interfaces/IDictionaryValues';

import * as actions from '../actions/dictionariesActions';
import { getDictionaryConstraintUuidKey } from '../common/getDictionaryConstraintUuidKey';
import { getDictionaryKey } from '../common/getDictionaryKey';
import { getDictionariesItemValueKey } from '../common/getDictionariesItemValueKey';
import { initialDictionariesState } from '../constants/initialDictionariesState';
import { DictionariesActionsEnum } from '../enums/DictionariesActionsEnum';
import { DictionaryNamesEnum } from '../enums/DictionaryNamesEnum';
import { DictionaryStatusesEnum } from '../enums/DictionaryStatusesEnum';
import { IDictionariesState } from '../interfaces/IDictionariesState';

export type DictionariesAction = ActionType<typeof actions>;

function getDictionariesItemGetRequestNewState(
  state: IDictionariesState,
  dictionaryName: DictionaryNamesEnum,
): IDictionariesState {
  return {
    ...state,
    items: {
      ...state.items,
      [dictionaryName]: {
        ...state.items[dictionaryName],
        status: DictionaryStatusesEnum.LOADING,
      },
    },
  };
}

function getDictionariesItemGetRequestClearState(
  state: IDictionariesState,
  dictionaryName: DictionaryNamesEnum,
): IDictionariesState {
  return {
    ...state,
    items: {
      ...state.items,
      [dictionaryName]: {
        data: undefined,
        status: DictionaryStatusesEnum.IDLE,
        error: undefined,
      },
    },
  };
}

function getDictionariesItemGetFailureNewState(
  state: IDictionariesState,
  dictionaryName: DictionaryNamesEnum,
): IDictionariesState {
  return {
    ...state,
    items: {
      ...state.items,
      [dictionaryName]: {
        ...state.items[dictionaryName],
        status: DictionaryStatusesEnum.FAILED,
      },
    },
  };
}

type DictionariesFilteringItemGetNewStateDictionaryNames =
  | DictionaryNamesEnum.POSITIONS
  | DictionaryNamesEnum.PERMIT_DOCS
  | DictionaryNamesEnum.PARTICIPANT_POSITIONS
  | DictionaryNamesEnum.KNM_KIND
  | DictionaryNamesEnum.DECISION_KIND
  | DictionaryNamesEnum.DECISION_KIND_ALL
  | DictionaryNamesEnum.PROF_KIND
  | DictionaryNamesEnum.RISK_CATEGORY
  | DictionaryNamesEnum.DANGER_CLASS
  | DictionaryNamesEnum.SUBJECT_TYPE
  | DictionaryNamesEnum.SUBJECT_KIND
  | DictionaryNamesEnum.SUBJECT_SUB_KIND
  | DictionaryNamesEnum.EVENT_TYPE
  | DictionaryNamesEnum.PM_PROF_EVENT_TYPE
  | DictionaryNamesEnum.SUPERVISION_TYPE
  | DictionaryNamesEnum.ACCEPTED_DOCUMENT
  | DictionaryNamesEnum.CHECKLIST
  | DictionaryNamesEnum.REQUIREMENT
  | DictionaryNamesEnum.RISK_INDICATORY
  | DictionaryNamesEnum.TARGET_ENTITY
  | DictionaryNamesEnum.TARGET_ENTITY_FOUNDER
  | DictionaryNamesEnum.KNM_TYPES
  | DictionaryNamesEnum.ERVK
  | DictionaryNamesEnum.CONDITION_TYPES
  | DictionaryNamesEnum.APPLICATION_CONDITION_SUBJECTS
  | DictionaryNamesEnum.SUPERVISION_TYPE_BY_SUBJECT_TYPE;

function getDictionariesFilteringItemGetRequestNewState(
  state: IDictionariesState,
  dictionaryName: DictionariesFilteringItemGetNewStateDictionaryNames,
  filterUUIDs?: string[],
): IDictionariesState {
  const groupKey: string = getDictionaryKey(dictionaryName, filterUUIDs);
  const dictionaryGroup = state.items[dictionaryName][groupKey] || {
    data: undefined,
    error: undefined,
  };

  return {
    ...state,
    items: {
      ...state.items,
      [dictionaryName]: {
        ...state.items[dictionaryName],
        [groupKey]: {
          ...dictionaryGroup,
          status: DictionaryStatusesEnum.LOADING,
        },
      },
    },
  };
}

function getDictionariesFilteringItemGetFailureNewState(
  state: IDictionariesState,
  dictionaryName: DictionariesFilteringItemGetNewStateDictionaryNames,

  filterUUIDs?: string[],
): IDictionariesState {
  const groupKey: string = getDictionaryKey(dictionaryName, filterUUIDs);

  return {
    ...state,
    items: {
      ...state.items,
      [dictionaryName]: {
        ...state.items[dictionaryName],
        [groupKey]: {
          ...state.items[dictionaryName][groupKey],
          status: DictionaryStatusesEnum.FAILED,
        },
      },
    },
  };
}

function filterByKnoAndSupervision(knoVersionIds: string[], supervisionVersionIds: string[]): string[] {
  // @ts-ignore
  return [...knoVersionIds, ...supervisionVersionIds]; // TODO sort
}

function getSuccessHandlerByKnoAndSupervision<T>(
  state: IDictionariesState,
  dictionaryName: DictionariesFilteringItemGetNewStateDictionaryNames,
  data: IDictionaryValues<T>,
  knoVersionIds: string[],
  supervisionVersionIds: string[],
) {
  const groupKey: string = getDictionaryKey(
    dictionaryName,
    filterByKnoAndSupervision(knoVersionIds, supervisionVersionIds),
  );

  const newState: IDictionariesState = {
    ...state,
    items: {
      ...state.items,
      [dictionaryName]: {
        ...state.items[dictionaryName],
        [groupKey]: {
          ...state.items[dictionaryName][groupKey],
          data,
          status: DictionaryStatusesEnum.SUCCEEDED,
        },
      },
    },
  };

  return newState;
}

export function dictionariesReducer(state = initialDictionariesState, action: DictionariesAction) {
  switch (action.type) {
    case DictionariesActionsEnum.DICTIONARIES_KNO_SUPERVISION_GET_REQUEST: {
      const { knoVersionId, supervisionVersionId } = action.payload;
      const groupKey: string = getDictionaryConstraintUuidKey([knoVersionId, supervisionVersionId]);
      const dictionaryGroup = state.knoSupervision[groupKey] || {
        data: undefined,
        status: undefined,
        error: undefined,
      };
      const newState: IDictionariesState = {
        ...state,
        knoSupervision: {
          ...state.knoSupervision,
          [groupKey]: {
            ...dictionaryGroup,
            status: DictionaryStatusesEnum.LOADING,
          },
        },
      };

      return newState;
    }
    case DictionariesActionsEnum.DICTIONARIES_KNO_SUPERVISION_GET_SUCCESS: {
      const { data, knoVersionId, supervisionVersionId } = action.payload;
      const groupKey: string = getDictionaryConstraintUuidKey([knoVersionId, supervisionVersionId]);
      const dictionaryGroup = state.knoSupervision[groupKey] || {
        data: undefined,
        status: undefined,
        error: undefined,
      };
      const newState: IDictionariesState = {
        ...state,
        knoSupervision: {
          ...state.knoSupervision,
          [groupKey]: {
            ...dictionaryGroup,
            data,
            status: DictionaryStatusesEnum.SUCCEEDED,
          },
        },
      };

      return newState;
    }
    case DictionariesActionsEnum.DICTIONARIES_KNO_SUPERVISION_GET_FAILURE: {
      const { knoVersionId, supervisionVersionId } = action.payload;
      const groupKey: string = getDictionaryConstraintUuidKey([knoVersionId, supervisionVersionId]);
      const dictionaryGroup = state.knoSupervision[groupKey] || {
        data: undefined,
        status: undefined,
        error: undefined,
      };
      const newState: IDictionariesState = {
        ...state,
        knoSupervision: {
          ...state.knoSupervision,
          [groupKey]: {
            ...dictionaryGroup,
            status: DictionaryStatusesEnum.FAILED,
          },
        },
      };

      return newState;
    }

    case DictionariesActionsEnum.DICTIONARIES_EVENT_UUID_GET_REQUEST: {
      const { knoVersionId, supervisionVersionId, knmKindVersionId } = action.payload;
      const groupKey: string = getDictionaryConstraintUuidKey([knoVersionId, supervisionVersionId, knmKindVersionId]);
      const dictionaryGroup = state.eventUuids[groupKey] || {
        data: undefined,
        status: undefined,
        error: undefined,
      };
      const newState: IDictionariesState = {
        ...state,
        eventUuids: {
          ...state.eventUuids,
          [groupKey]: {
            ...dictionaryGroup,
            status: DictionaryStatusesEnum.LOADING,
          },
        },
      };

      return newState;
    }
    case DictionariesActionsEnum.DICTIONARIES_EVENT_UUID_GET_SUCCESS: {
      const { data, knoVersionId, supervisionVersionId, knmKindVersionId } = action.payload;
      const groupKey: string = getDictionaryConstraintUuidKey([knoVersionId, supervisionVersionId, knmKindVersionId]);
      const dictionaryGroup = state.eventUuids[groupKey] || {
        data: undefined,
        status: undefined,
        error: undefined,
      };
      const newState: IDictionariesState = {
        ...state,
        eventUuids: {
          ...state.eventUuids,
          [groupKey]: {
            ...dictionaryGroup,
            data,
            status: DictionaryStatusesEnum.SUCCEEDED,
          },
        },
      };

      return newState;
    }
    case DictionariesActionsEnum.DICTIONARIES_EVENT_UUID_GET_FAILURE: {
      const { knoVersionId, supervisionVersionId, knmKindVersionId } = action.payload;
      const groupKey: string = getDictionaryConstraintUuidKey([knoVersionId, supervisionVersionId, knmKindVersionId]);
      const dictionaryGroup = state.eventUuids[groupKey] || {
        data: undefined,
        status: undefined,
        error: undefined,
      };
      const newState: IDictionariesState = {
        ...state,
        eventUuids: {
          ...state.eventUuids,
          [groupKey]: {
            ...dictionaryGroup,
            status: DictionaryStatusesEnum.FAILED,
          },
        },
      };

      return newState;
    }

    case DictionariesActionsEnum.DICTIONARIES_EVENT_HAS_UNPLANNED_ACTIVITY_GET_REQUEST: {
      const { knoVersionId, supervisionVersionId, knmKindVersionId } = action.payload;
      const groupKey = getDictionaryConstraintUuidKey([knoVersionId, supervisionVersionId, knmKindVersionId]);
      const dictionaryGroup = state.eventHasUnplannedActivity[groupKey] ?? {
        data: undefined,
        status: undefined,
        error: undefined,
      };
      const newState: IDictionariesState = {
        ...state,
        eventHasUnplannedActivity: {
          ...state.eventHasUnplannedActivity,
          [groupKey]: {
            ...dictionaryGroup,
            status: DictionaryStatusesEnum.LOADING,
          },
        },
      };

      return newState;
    }
    case DictionariesActionsEnum.DICTIONARIES_EVENT_HAS_UNPLANNED_ACTIVITY_GET_SUCCESS: {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      const { data, knoVersionId, supervisionVersionId, knmKindVersionId } = action.payload;
      const groupKey = getDictionaryConstraintUuidKey([knoVersionId, supervisionVersionId, knmKindVersionId]);
      const dictionaryGroup = state.eventHasUnplannedActivity[groupKey] ?? {
        data: undefined,
        status: undefined,
        error: undefined,
      };

      const newState: IDictionariesState = {
        ...state,
        eventHasUnplannedActivity: {
          ...state.eventHasUnplannedActivity,
          [groupKey]: {
            ...dictionaryGroup,
            data,
            status: DictionaryStatusesEnum.SUCCEEDED,
          },
        },
      };

      return newState;
    }
    case DictionariesActionsEnum.DICTIONARIES_EVENT_HAS_UNPLANNED_ACTIVITY_GET_FAILURE: {
      const { knoVersionId, supervisionVersionId, knmKindVersionId } = action.payload;
      const groupKey = getDictionaryConstraintUuidKey([knoVersionId, supervisionVersionId, knmKindVersionId]);
      const dictionaryGroup = state.eventHasUnplannedActivity[groupKey] ?? {
        data: undefined,
        status: undefined,
        error: undefined,
      };
      const newState: IDictionariesState = {
        ...state,
        eventHasUnplannedActivity: {
          ...state.eventHasUnplannedActivity,
          [groupKey]: {
            ...dictionaryGroup,
            status: DictionaryStatusesEnum.FAILED,
          },
        },
      };

      return newState;
    }

    case DictionariesActionsEnum.DICTIONARIES_GET_REQUEST: {
      const newState: IDictionariesState = {
        ...state,
        itemsList: {
          ...state.itemsList,
          status: DictionaryStatusesEnum.LOADING,
        },
      };

      return newState;
    }
    case DictionariesActionsEnum.DICTIONARIES_GET_SUCCESS: {
      const { data } = action.payload;
      const newState: IDictionariesState = {
        ...state,
        itemsList: {
          ...state.itemsList,
          data,
          status: DictionaryStatusesEnum.SUCCEEDED,
        },
      };

      return newState;
    }
    case DictionariesActionsEnum.DICTIONARIES_GET_FAILURE: {
      const newState: IDictionariesState = {
        ...state,
        itemsList: {
          ...state.itemsList,
          status: DictionaryStatusesEnum.FAILED,
        },
      };

      return newState;
    }

    case DictionariesActionsEnum.DICTIONARIES_ITEM_VALUES_GET_REQUEST: {
      const { dictionaryName } = action.payload;
      const key: string = getDictionaryKey(dictionaryName);
      const newState: IDictionariesState = {
        ...state,
        itemsValues: {
          ...state.itemsValues,
          [key]: {
            ...state.itemsValues[key],
            status: DictionaryStatusesEnum.LOADING,
          },
        },
      };

      return newState;
    }
    case DictionariesActionsEnum.DICTIONARIES_ITEM_VALUES_GET_SUCCESS: {
      const { dictionaryName, data } = action.payload;
      const key = getDictionaryKey(dictionaryName);
      const newState: IDictionariesState = {
        ...state,
        itemsValues: {
          ...state.itemsValues,
          [key]: {
            ...state.itemsValues[key],
            data,
            status: DictionaryStatusesEnum.SUCCEEDED,
          },
        },
      };

      return newState;
    }
    case DictionariesActionsEnum.DICTIONARIES_ITEM_VALUES_GET_FAILURE: {
      const { dictionaryName } = action.payload;
      const key = getDictionaryKey(dictionaryName);
      const newState: IDictionariesState = {
        ...state,
        itemsValues: {
          ...state.itemsValues,
          [key]: {
            ...state.itemsValues[key],
            status: DictionaryStatusesEnum.FAILED,
          },
        },
      };

      return newState;
    }

    case DictionariesActionsEnum.DICTIONARIES_ITEM_VALUE_GET_REQUEST: {
      const { dictionaryUUID, dictionaryVersionUUID } = action.payload;
      const key = getDictionariesItemValueKey(dictionaryUUID);

      const newState: IDictionariesState = {
        ...state,
        itemValue: {
          ...state.itemValue,
          [key]: {
            ...state.itemValue[key],
            [dictionaryVersionUUID]: {
              ...state.itemValue[key][dictionaryVersionUUID],
              status: DictionaryStatusesEnum.LOADING,
            },
          },
        },
      };

      return newState;
    }
    case DictionariesActionsEnum.DICTIONARIES_ITEM_VALUE_GET_SUCCESS: {
      const { dictionaryUUID, dictionaryVersionUUID, data } = action.payload;
      const key = getDictionariesItemValueKey(dictionaryUUID);
      const newState: IDictionariesState = {
        ...state,
        itemValue: {
          ...state.itemValue,
          [key]: {
            ...state.itemValue[key],
            [dictionaryVersionUUID]: {
              ...state.itemValue[key][dictionaryVersionUUID],
              data,
              status: DictionaryStatusesEnum.SUCCEEDED,
            },
          },
        },
      };

      return newState;
    }
    case DictionariesActionsEnum.DICTIONARIES_ITEM_VALUE_GET_FAILURE: {
      const { dictionaryUUID, dictionaryVersionUUID } = action.payload;
      const key = getDictionariesItemValueKey(dictionaryUUID);
      const newState: IDictionariesState = {
        ...state,
        itemValue: {
          ...state.itemValue,
          [key]: {
            ...state.itemValue[key],
            [dictionaryVersionUUID]: {
              ...state.itemValue[key][dictionaryVersionUUID],
              status: DictionaryStatusesEnum.FAILED,
            },
          },
        },
      };

      return newState;
    }
    case DictionariesActionsEnum.DICTIONARIES_ITEM_VALUE_GET_NO_DATA: {
      const { dictionaryUUID, dictionaryVersionUUID } = action.payload;
      const key = getDictionariesItemValueKey(dictionaryUUID);
      const prevGroup = state.itemValue[key] || {};
      const { [dictionaryVersionUUID]: _omit, ...restGroup } = prevGroup;

      return {
        ...state,
        itemValue: {
          ...state.itemValue,
          [key]: restGroup,
        },
      };
    }

    case DictionariesActionsEnum.DICTIONARIES_ITEM_VALUE_GET_REQUEST_WITH_CALLBACK: {
      const { dictionaryUUID, dictionaryVersionUUID } = action.payload;
      const key = getDictionariesItemValueKey(dictionaryUUID);

      const newState: IDictionariesState = {
        ...state,
        itemValue: {
          ...state.itemValue,
          [key]: {
            ...state.itemValue[key],
            [dictionaryVersionUUID]: {
              ...state.itemValue[key][dictionaryVersionUUID],
              status: DictionaryStatusesEnum.LOADING,
            },
          },
        },
      };

      return newState;
    }
    case DictionariesActionsEnum.DICTIONARIES_ITEM_VALUE_GET_SUCCESS_WITH_CALLBACK: {
      const { dictionaryUUID, dictionaryVersionUUID, data } = action.payload;
      const key = getDictionariesItemValueKey(dictionaryUUID);
      const newState: IDictionariesState = {
        ...state,
        itemValue: {
          ...state.itemValue,
          [key]: {
            ...state.itemValue[key],
            [dictionaryVersionUUID]: {
              ...state.itemValue[key][dictionaryVersionUUID],
              data,
              status: DictionaryStatusesEnum.SUCCEEDED,
            },
          },
        },
      };

      return newState;
    }
    case DictionariesActionsEnum.DICTIONARIES_ITEM_VALUE_GET_FAILURE_WITH_CALLBACK: {
      const { dictionaryUUID, dictionaryVersionUUID } = action.payload;
      const key = getDictionariesItemValueKey(dictionaryUUID);
      const newState: IDictionariesState = {
        ...state,
        itemValue: {
          ...state.itemValue,
          [key]: {
            ...state.itemValue[key],
            [dictionaryVersionUUID]: {
              ...state.itemValue[key][dictionaryVersionUUID],
              status: DictionaryStatusesEnum.FAILED,
            },
          },
        },
      };

      return newState;
    }

    case DictionariesActionsEnum.DICTIONARIES_KNO_ORGANIZATIONS_ITEM_GET_REQUEST: {
      const dictionaryName = DictionaryNamesEnum.KNO_ORGANIZATIONS;

      return getDictionariesItemGetRequestNewState(state, dictionaryName);
    }

    case DictionariesActionsEnum.DICTIONARIES_FEDERAL_LAWS_GET_REQUEST: {
      const newState: IDictionariesState = {
        ...state,
        federalLawsStatus: DictionaryStatusesEnum.LOADING,
      };

      return newState;
    }
    case DictionariesActionsEnum.DICTIONARIES_FEDERAL_LAWS_GET_SUCCESS: {
      const { data } = action.payload;
      const newState: IDictionariesState = {
        ...state,
        federalLaws: data,
        federalLawsStatus: DictionaryStatusesEnum.SUCCEEDED,
      };

      return newState;
    }
    case DictionariesActionsEnum.DICTIONARIES_FEDERAL_LAWS_GET_FAILURE: {
      const newState: IDictionariesState = {
        ...state,
        federalLawsStatus: DictionaryStatusesEnum.FAILED,
      };

      return newState;
    }
    case DictionariesActionsEnum.DICTIONARIES_KNO_ORGANIZATIONS_ITEM_GET_SUCCESS: {
      const { data } = action.payload;
      const dictionaryName = DictionaryNamesEnum.KNO_ORGANIZATIONS;
      const newState: IDictionariesState = {
        ...state,
        items: {
          ...state.items,
          [dictionaryName]: {
            ...state.items[dictionaryName],
            data,
            status: DictionaryStatusesEnum.SUCCEEDED,
          },
        },
      };

      return newState;
    }
    case DictionariesActionsEnum.DICTIONARIES_KNO_ORGANIZATIONS_ITEM_GET_FAILURE: {
      const dictionaryName = DictionaryNamesEnum.KNO_ORGANIZATIONS;

      return getDictionariesItemGetFailureNewState(state, dictionaryName);
    }

    case DictionariesActionsEnum.DICTIONARIES_POSITIONS_ITEM_GET_REQUEST: {
      const { knoSupervisionUuid } = action.payload;
      const dictionaryName = DictionaryNamesEnum.POSITIONS;

      return getDictionariesFilteringItemGetRequestNewState(state, dictionaryName, [knoSupervisionUuid || '']);
    }
    case DictionariesActionsEnum.DICTIONARIES_POSITIONS_ITEM_GET_SUCCESS: {
      const { data, knoSupervisionUuid } = action.payload;
      const dictionaryName = DictionaryNamesEnum.POSITIONS;
      const groupKey: string = getDictionaryKey(dictionaryName, [knoSupervisionUuid || '']);
      const newState: IDictionariesState = {
        ...state,
        items: {
          ...state.items,
          [dictionaryName]: {
            ...state.items[dictionaryName],
            [groupKey]: {
              ...state.items[dictionaryName][groupKey],
              data,
              status: DictionaryStatusesEnum.SUCCEEDED,
            },
          },
        },
      };

      return newState;
    }
    case DictionariesActionsEnum.DICTIONARIES_POSITIONS_ITEM_GET_FAILURE: {
      const { knoSupervisionUuid } = action.payload;
      const dictionaryName = DictionaryNamesEnum.POSITIONS;

      return getDictionariesFilteringItemGetFailureNewState(state, dictionaryName, [knoSupervisionUuid || '']);
    }

    case DictionariesActionsEnum.DICTIONARIES_PERMIT_DOCS_ITEM_GET_REQUEST: {
      const { controlKindUuid } = action.payload;
      const dictionaryName = DictionaryNamesEnum.PERMIT_DOCS;

      return getDictionariesFilteringItemGetRequestNewState(state, dictionaryName, [controlKindUuid || '']);
    }
    case DictionariesActionsEnum.DICTIONARIES_PERMIT_DOCS_ITEM_GET_SUCCESS: {
      const { data, controlKindUuid } = action.payload;
      const dictionaryName = DictionaryNamesEnum.PERMIT_DOCS;
      const groupKey: string = getDictionaryKey(dictionaryName, [controlKindUuid || '']);
      const newState: IDictionariesState = {
        ...state,
        items: {
          ...state.items,
          [dictionaryName]: {
            ...state.items[dictionaryName],
            [groupKey]: {
              ...state.items[dictionaryName][groupKey],
              data,
              status: DictionaryStatusesEnum.SUCCEEDED,
            },
          },
        },
      };

      return newState;
    }
    case DictionariesActionsEnum.DICTIONARIES_PERMIT_DOCS_ITEM_GET_FAILURE: {
      const { controlKindUuid } = action.payload;
      const dictionaryName = DictionaryNamesEnum.PERMIT_DOCS;

      return getDictionariesFilteringItemGetFailureNewState(state, dictionaryName, [controlKindUuid || '']);
    }

    case DictionariesActionsEnum.DICTIONARIES_PARTICIPANT_POSITIONS_ITEM_GET_REQUEST: {
      const { knoSupervisionUuid } = action.payload;
      const dictionaryName = DictionaryNamesEnum.PARTICIPANT_POSITIONS;

      return getDictionariesFilteringItemGetRequestNewState(state, dictionaryName, [knoSupervisionUuid || '']);
    }
    case DictionariesActionsEnum.DICTIONARIES_PARTICIPANT_POSITIONS_ITEM_GET_SUCCESS: {
      const { data, knoSupervisionUuid } = action.payload;
      const dictionaryName = DictionaryNamesEnum.PARTICIPANT_POSITIONS;
      const groupKey: string = getDictionaryKey(dictionaryName, [knoSupervisionUuid || '']);
      const newState: IDictionariesState = {
        ...state,
        items: {
          ...state.items,
          [dictionaryName]: {
            ...state.items[dictionaryName],
            [groupKey]: {
              ...state.items[dictionaryName][groupKey],
              data,
              status: DictionaryStatusesEnum.SUCCEEDED,
            },
          },
        },
      };

      return newState;
    }
    case DictionariesActionsEnum.DICTIONARIES_PARTICIPANT_POSITIONS_ITEM_GET_FAILURE: {
      const { knoSupervisionUuid } = action.payload;
      const dictionaryName = DictionaryNamesEnum.PARTICIPANT_POSITIONS;

      return getDictionariesFilteringItemGetFailureNewState(state, dictionaryName, [knoSupervisionUuid || '']);
    }

    case DictionariesActionsEnum.DICTIONARIES_KNM_KIND_ITEM_GET_REQUEST: {
      const { triplet } = action.payload;
      const dictionaryName = DictionaryNamesEnum.KNM_KIND;
      // @ts-ignore
      const filter: string[] = triplet?.conditionUuid
        ? [triplet?.knoVersionId, triplet?.supervisionVersionId, triplet?.conditionUuid, triplet?.orderVersionId]
        : [triplet?.knoVersionId, triplet?.supervisionVersionId, triplet?.orderVersionId];

      return getDictionariesFilteringItemGetRequestNewState(state, dictionaryName, triplet ? filter : undefined);
    }
    case DictionariesActionsEnum.DICTIONARIES_KNM_KIND_ITEM_GET_SUCCESS: {
      const { data, triplet } = action.payload;
      const dictionaryName = DictionaryNamesEnum.KNM_KIND;
      // @ts-ignore
      const filter: string[] = triplet?.conditionUuid
        ? [triplet?.knoVersionId, triplet?.supervisionVersionId, triplet?.conditionUuid, triplet?.orderVersionId]
        : [triplet?.knoVersionId, triplet?.supervisionVersionId, triplet?.orderVersionId];
      const groupKey: string = getDictionaryKey(dictionaryName, triplet ? filter : undefined);
      const newState: IDictionariesState = {
        ...state,
        items: {
          ...state.items,
          [dictionaryName]: {
            ...state.items[dictionaryName],
            [groupKey]: {
              ...state.items[dictionaryName][groupKey],
              data,
              status: DictionaryStatusesEnum.SUCCEEDED,
            },
          },
        },
      };

      return newState;
    }
    case DictionariesActionsEnum.DICTIONARIES_KNM_KIND_ITEM_GET_FAILURE: {
      const { triplet } = action.payload;
      const dictionaryName = DictionaryNamesEnum.KNM_KIND;
      // @ts-ignore
      const filter: string[] = triplet?.conditionUuid
        ? [triplet?.knoVersionId, triplet?.supervisionVersionId, triplet?.conditionUuid]
        : [triplet?.knoVersionId, triplet?.supervisionVersionId];

      return getDictionariesFilteringItemGetFailureNewState(state, dictionaryName, triplet ? filter : undefined);
    }

    case DictionariesActionsEnum.DICTIONARIES_DECISION_KIND_ITEM_GET_REQUEST: {
      const { pair } = action.payload;
      const dictionaryName = DictionaryNamesEnum.DECISION_KIND;
      // @ts-ignore
      const filter: string[] = [pair?.knoVersionId, pair?.supervisionVersionId];

      return getDictionariesFilteringItemGetRequestNewState(state, dictionaryName, pair ? filter : undefined);
    }
    case DictionariesActionsEnum.DICTIONARIES_DECISION_KIND_ITEM_GET_SUCCESS: {
      const { data, pair } = action.payload;

      const dictionaryName = DictionaryNamesEnum.DECISION_KIND;
      // @ts-ignore
      const filter: string[] = [pair?.knoVersionId, pair?.supervisionVersionId, pair?.orderVersionId];
      const groupKey: string = getDictionaryKey(dictionaryName, pair ? filter : undefined);
      const newState: IDictionariesState = {
        ...state,
        items: {
          ...state.items,
          [dictionaryName]: {
            ...state.items[dictionaryName],
            [groupKey]: {
              ...state.items[dictionaryName][groupKey],
              data,
              status: DictionaryStatusesEnum.SUCCEEDED,
            },
          },
        },
      };

      return newState;
    }
    case DictionariesActionsEnum.DICTIONARIES_DECISION_KIND_ITEM_GET_FAILURE: {
      const { pair } = action.payload;
      const dictionaryName = DictionaryNamesEnum.DECISION_KIND;
      // @ts-ignore
      const filter: string[] = [pair?.knoVersionId, pair?.supervisionVersionId];

      return getDictionariesFilteringItemGetFailureNewState(state, dictionaryName, pair ? filter : undefined);
    }

    case DictionariesActionsEnum.DICTIONARIES_DECISION_KIND_ALL_ITEM_GET_REQUEST: {
      const { pair } = action.payload;
      const dictionaryName = DictionaryNamesEnum.DECISION_KIND_ALL;
      // @ts-ignore
      const filter: string[] = [pair?.knoVersionId, pair?.supervisionVersionId];

      return getDictionariesFilteringItemGetRequestNewState(state, dictionaryName, pair ? filter : undefined);
    }
    case DictionariesActionsEnum.DICTIONARIES_DECISION_KIND_ALL_ITEM_GET_SUCCESS: {
      const { data, pair } = action.payload;

      const dictionaryName = DictionaryNamesEnum.DECISION_KIND_ALL;
      // @ts-ignore
      const filter: string[] = [pair?.knoVersionId, pair?.supervisionVersionId, pair?.orderVersionId];
      const groupKey: string = getDictionaryKey(dictionaryName, pair ? filter : undefined);
      const newState: IDictionariesState = {
        ...state,
        items: {
          ...state.items,
          [dictionaryName]: {
            ...state.items[dictionaryName],
            [groupKey]: {
              ...state.items[dictionaryName][groupKey],
              data,
              status: DictionaryStatusesEnum.SUCCEEDED,
            },
          },
        },
      };

      return newState;
    }
    case DictionariesActionsEnum.DICTIONARIES_DECISION_KIND_ALL_ITEM_GET_FAILURE: {
      const { pair } = action.payload;
      const dictionaryName = DictionaryNamesEnum.DECISION_KIND_ALL;
      // @ts-ignore
      const filter: string[] = [pair?.knoVersionId, pair?.supervisionVersionId];

      return getDictionariesFilteringItemGetFailureNewState(state, dictionaryName, pair ? filter : undefined);
    }

    case DictionariesActionsEnum.DICTIONARIES_PROF_KIND_ITEM_GET_REQUEST: {
      const { pair, conditionUuid } = action.payload;
      const dictionaryName = DictionaryNamesEnum.PROF_KIND;
      const filter: string[] = [pair?.knoVersionId || '', pair?.supervisionVersionId || '', conditionUuid || ''];

      return getDictionariesFilteringItemGetRequestNewState(state, dictionaryName, pair ? filter : undefined);
    }
    case DictionariesActionsEnum.DICTIONARIES_PROF_KIND_ITEM_GET_SUCCESS: {
      const { data, pair, conditionUuid } = action.payload;
      const dictionaryName = DictionaryNamesEnum.PROF_KIND;
      const filter: string[] = [
        pair?.knoVersionId || '',
        pair?.supervisionVersionId || '',
        pair?.orderVersionId || '',
        conditionUuid || '',
      ];
      const groupKey: string = getDictionaryKey(dictionaryName, pair ? filter : undefined);
      const newState: IDictionariesState = {
        ...state,
        items: {
          ...state.items,
          [dictionaryName]: {
            ...state.items[dictionaryName],
            [groupKey]: {
              ...state.items[dictionaryName][groupKey],
              data,
              status: DictionaryStatusesEnum.SUCCEEDED,
            },
          },
        },
      };

      return newState;
    }
    case DictionariesActionsEnum.DICTIONARIES_PROF_KIND_ITEM_GET_FAILURE: {
      const { pair, conditionUuid } = action.payload;
      const dictionaryName = DictionaryNamesEnum.PROF_KIND;
      const filter: string[] = [pair?.knoVersionId || '', pair?.supervisionVersionId || '', conditionUuid || ''];

      return getDictionariesFilteringItemGetFailureNewState(state, dictionaryName, pair ? filter : undefined);
    }

    case DictionariesActionsEnum.DICTIONARIES_RISK_CATEGORY_ITEM_GET_REQUEST: {
      const { knoSupervisionUuid, conditionUuid, orderVersionUuid, knmKindUuid, isPlanned } = action.payload;
      const dictionaryName = DictionaryNamesEnum.RISK_CATEGORY;

      return getDictionariesFilteringItemGetRequestNewState(state, dictionaryName, [
        knoSupervisionUuid || '',
        conditionUuid || '',
        orderVersionUuid || '',
        knmKindUuid || '',
        typeof isPlanned === 'boolean' ? String(isPlanned) : '',
      ]);
    }
    case DictionariesActionsEnum.DICTIONARIES_RISK_CATEGORY_ITEM_GET_SUCCESS: {
      const { data, knoSupervisionUuid, conditionUuid, orderVersionUuid, knmKindUuid, isPlanned } = action.payload;
      const dictionaryName = DictionaryNamesEnum.RISK_CATEGORY;
      const groupKey: string = getDictionaryKey(dictionaryName, [
        knoSupervisionUuid || '',
        conditionUuid || '',
        orderVersionUuid || '',
        knmKindUuid || '',
        typeof isPlanned === 'boolean' ? String(isPlanned) : '',
      ]);
      const newState: IDictionariesState = {
        ...state,
        items: {
          ...state.items,
          [dictionaryName]: {
            ...state.items[dictionaryName],
            [groupKey]: {
              ...state.items[dictionaryName][groupKey],
              data,
              status: DictionaryStatusesEnum.SUCCEEDED,
            },
          },
        },
      };

      return newState;
    }
    case DictionariesActionsEnum.DICTIONARIES_RISK_CATEGORY_ITEM_GET_FAILURE: {
      const { knoSupervisionUuid, conditionUuid, orderVersionUuid, knmKindUuid, isPlanned } = action.payload;
      const dictionaryName = DictionaryNamesEnum.RISK_CATEGORY;

      return getDictionariesFilteringItemGetFailureNewState(state, dictionaryName, [
        knoSupervisionUuid || '',
        conditionUuid || '',
        orderVersionUuid || '',
        knmKindUuid || '',
        typeof isPlanned === 'boolean' ? String(isPlanned) : '',
      ]);
    }

    case DictionariesActionsEnum.DICTIONARIES_DANGER_CLASS_ITEM_GET_REQUEST: {
      const { knoSupervisionUuid, conditionUuid, orderVersionUuid, isPlanned } = action.payload;
      const dictionaryName = DictionaryNamesEnum.DANGER_CLASS;

      return getDictionariesFilteringItemGetRequestNewState(state, dictionaryName, [
        knoSupervisionUuid || '',
        conditionUuid || '',
        orderVersionUuid || '',
        typeof isPlanned === 'boolean' ? String(isPlanned) : '',
      ]);
    }
    case DictionariesActionsEnum.DICTIONARIES_DANGER_CLASS_ITEM_GET_SUCCESS: {
      const { data, knoSupervisionUuid, conditionUuid, orderVersionUuid, isPlanned } = action.payload;
      const dictionaryName = DictionaryNamesEnum.DANGER_CLASS;
      const groupKey: string = getDictionaryKey(dictionaryName, [
        knoSupervisionUuid || '',
        conditionUuid || '',
        orderVersionUuid || '',
        typeof isPlanned === 'boolean' ? String(isPlanned) : '',
      ]);
      const newState: IDictionariesState = {
        ...state,
        items: {
          ...state.items,
          [dictionaryName]: {
            ...state.items[dictionaryName],
            [groupKey]: {
              ...state.items[dictionaryName][groupKey],
              data,
              status: DictionaryStatusesEnum.SUCCEEDED,
            },
          },
        },
      };

      return newState;
    }
    case DictionariesActionsEnum.DICTIONARIES_DANGER_CLASS_ITEM_GET_FAILURE: {
      const { knoSupervisionUuid, conditionUuid, orderVersionUuid, isPlanned } = action.payload;
      const dictionaryName = DictionaryNamesEnum.DANGER_CLASS;

      return getDictionariesFilteringItemGetFailureNewState(state, dictionaryName, [
        knoSupervisionUuid || '',
        conditionUuid || '',
        orderVersionUuid || '',
        typeof isPlanned === 'boolean' ? String(isPlanned) : '',
      ]);
    }

    case DictionariesActionsEnum.DICTIONARIES_SUBJECT_TYPE_ITEM_GET_REQUEST: {
      const { knoSupervisionUuid, conditionUuid, orderVersionUuid } = action.payload;
      const dictionaryName = DictionaryNamesEnum.SUBJECT_TYPE;

      return getDictionariesFilteringItemGetRequestNewState(state, dictionaryName, [
        knoSupervisionUuid || '',
        conditionUuid || '',
        orderVersionUuid || '',
      ]);
    }
    case DictionariesActionsEnum.DICTIONARIES_SUBJECT_TYPE_ITEM_GET_SUCCESS: {
      const { data, knoSupervisionUuid, conditionUuid, orderVersionUuid } = action.payload;
      const dictionaryName = DictionaryNamesEnum.SUBJECT_TYPE;
      const groupKey: string = getDictionaryKey(dictionaryName, [
        knoSupervisionUuid || '',
        conditionUuid || '',
        orderVersionUuid || '',
      ]);
      const newState: IDictionariesState = {
        ...state,
        items: {
          ...state.items,
          [dictionaryName]: {
            ...state.items[dictionaryName],
            [groupKey]: {
              ...state.items[dictionaryName][groupKey],
              data,
              status: DictionaryStatusesEnum.SUCCEEDED,
            },
          },
        },
      };

      return newState;
    }
    case DictionariesActionsEnum.DICTIONARIES_SUBJECT_TYPE_ITEM_GET_FAILURE: {
      const { knoSupervisionUuid, conditionUuid, orderVersionUuid } = action.payload;
      const dictionaryName = DictionaryNamesEnum.SUBJECT_TYPE;

      return getDictionariesFilteringItemGetFailureNewState(state, dictionaryName, [
        knoSupervisionUuid || '',
        conditionUuid || '',
        orderVersionUuid || '',
      ]);
    }

    case DictionariesActionsEnum.DICTIONARIES_SUBJECT_KIND_ITEM_GET_REQUEST: {
      const { knoSupervisionUuid, conditionUuid, orderVersionUuid, objectTypeDictVersionId } = action.payload;
      const dictionaryName = DictionaryNamesEnum.SUBJECT_KIND;

      return getDictionariesFilteringItemGetRequestNewState(state, dictionaryName, [
        knoSupervisionUuid || '',
        conditionUuid || '',
        orderVersionUuid || '',
        objectTypeDictVersionId || '',
      ]);
    }
    case DictionariesActionsEnum.DICTIONARIES_SUBJECT_KIND_ITEM_GET_SUCCESS: {
      const { data, knoSupervisionUuid, conditionUuid, orderVersionUuid, objectTypeDictVersionId } = action.payload;
      const dictionaryName = DictionaryNamesEnum.SUBJECT_KIND;
      const groupKey: string = getDictionaryKey(dictionaryName, [
        knoSupervisionUuid || '',
        conditionUuid || '',
        orderVersionUuid || '',
        objectTypeDictVersionId || '',
      ]);
      const newState: IDictionariesState = {
        ...state,
        items: {
          ...state.items,
          [dictionaryName]: {
            ...state.items[dictionaryName],
            [groupKey]: {
              ...state.items[dictionaryName][groupKey],
              data,
              status: DictionaryStatusesEnum.SUCCEEDED,
            },
          },
        },
      };

      return newState;
    }
    case DictionariesActionsEnum.DICTIONARIES_SUBJECT_KIND_ITEM_GET_FAILURE: {
      const { knoSupervisionUuid, conditionUuid, orderVersionUuid, objectTypeDictVersionId } = action.payload;
      const dictionaryName = DictionaryNamesEnum.SUBJECT_KIND;

      return getDictionariesFilteringItemGetFailureNewState(state, dictionaryName, [
        knoSupervisionUuid || '',
        conditionUuid || '',
        orderVersionUuid || '',
        objectTypeDictVersionId || '',
      ]);
    }

    case DictionariesActionsEnum.DICTIONARIES_SUBJECT_SUB_KIND_ITEM_GET_REQUEST: {
      const { knoSupervisionUuid, conditionUuid, orderVersionUuid, objectTypeDictVersionId } = action.payload;
      const dictionaryName = DictionaryNamesEnum.SUBJECT_SUB_KIND;

      return getDictionariesFilteringItemGetRequestNewState(state, dictionaryName, [
        knoSupervisionUuid || '',
        conditionUuid || '',
        orderVersionUuid || '',
        objectTypeDictVersionId || '',
      ]);
    }
    case DictionariesActionsEnum.DICTIONARIES_SUBJECT_SUB_KIND_ITEM_GET_SUCCESS: {
      const { data, knoSupervisionUuid, conditionUuid, orderVersionUuid, objectTypeDictVersionId } = action.payload;
      const dictionaryName = DictionaryNamesEnum.SUBJECT_SUB_KIND;
      const groupKey: string = getDictionaryKey(dictionaryName, [
        knoSupervisionUuid || '',
        conditionUuid || '',
        orderVersionUuid || '',
        objectTypeDictVersionId || '',
      ]);
      const newState: IDictionariesState = {
        ...state,
        items: {
          ...state.items,
          [dictionaryName]: {
            ...state.items[dictionaryName],
            [groupKey]: {
              ...state.items[dictionaryName][groupKey],
              data,
              status: DictionaryStatusesEnum.SUCCEEDED,
            },
          },
        },
      };

      return newState;
    }
    case DictionariesActionsEnum.DICTIONARIES_SUBJECT_SUB_KIND_ITEM_GET_FAILURE: {
      const { knoSupervisionUuid, conditionUuid, orderVersionUuid, objectTypeDictVersionId } = action.payload;
      const dictionaryName = DictionaryNamesEnum.SUBJECT_SUB_KIND;

      return getDictionariesFilteringItemGetFailureNewState(state, dictionaryName, [
        knoSupervisionUuid || '',
        conditionUuid || '',
        orderVersionUuid || '',
        objectTypeDictVersionId || '',
      ]);
    }

    case DictionariesActionsEnum.DICTIONARIES_EVENT_TYPE_ITEM_GET_REQUEST: {
      const { uniqueUuid, conditionUuid, orderVersionUuid } = action.payload;
      const dictionaryName = DictionaryNamesEnum.EVENT_TYPE;

      return getDictionariesFilteringItemGetRequestNewState(state, dictionaryName, [
        uniqueUuid || '',
        conditionUuid || '',
        orderVersionUuid || '',
      ]);
    }
    case DictionariesActionsEnum.DICTIONARIES_EVENT_TYPE_ITEM_GET_SUCCESS: {
      const { data, uniqueUuid, conditionUuid, orderVersionId } = action.payload;
      const dictionaryName = DictionaryNamesEnum.EVENT_TYPE;
      const groupKey: string = getDictionaryKey(dictionaryName, [
        uniqueUuid || '',
        conditionUuid || '',
        orderVersionId || '',
      ]);
      const newState: IDictionariesState = {
        ...state,
        items: {
          ...state.items,
          [dictionaryName]: {
            ...state.items[dictionaryName],
            [groupKey]: {
              ...state.items[dictionaryName][groupKey],
              data,
              status: DictionaryStatusesEnum.SUCCEEDED,
            },
          },
        },
      };

      return newState;
    }
    case DictionariesActionsEnum.DICTIONARIES_EVENT_TYPE_ITEM_GET_FAILURE: {
      const { uniqueUuid, conditionUuid, orderVersionUuid } = action.payload;
      const dictionaryName = DictionaryNamesEnum.EVENT_TYPE;

      return getDictionariesFilteringItemGetFailureNewState(state, dictionaryName, [
        uniqueUuid || '',
        conditionUuid || '',
        orderVersionUuid || '',
      ]);
    }

    case DictionariesActionsEnum.DICTIONARIES_PM_PROF_EVENT_TYPE_ITEM_GET_REQUEST: {
      const { isUnableToRefuse } = action.payload;
      const dictionaryName = DictionaryNamesEnum.PM_PROF_EVENT_TYPE;

      return getDictionariesFilteringItemGetRequestNewState(state, dictionaryName, [
        isUnableToRefuse?.toString() || 'false',
      ]);
    }
    case DictionariesActionsEnum.DICTIONARIES_PM_PROF_EVENT_TYPE_ITEM_GET_SUCCESS: {
      const { data, isUnableToRefuse } = action.payload;
      const dictionaryName = DictionaryNamesEnum.PM_PROF_EVENT_TYPE;
      const groupKey: string = getDictionaryKey(dictionaryName, [isUnableToRefuse?.toString() || 'false']);

      const newState: IDictionariesState = {
        ...state,
        items: {
          ...state.items,
          [dictionaryName]: {
            ...state.items[dictionaryName],
            [groupKey]: {
              ...state.items[dictionaryName][groupKey],
              data,
              status: DictionaryStatusesEnum.SUCCEEDED,
            },
          },
        },
      };

      return newState;
    }
    case DictionariesActionsEnum.DICTIONARIES_PM_PROF_EVENT_TYPE_ITEM_GET_FAILURE: {
      const { isUnableToRefuse } = action.payload;
      const dictionaryName = DictionaryNamesEnum.PM_PROF_EVENT_TYPE;

      return getDictionariesFilteringItemGetFailureNewState(state, dictionaryName, [
        isUnableToRefuse?.toString() || '',
      ]);
    }

    case DictionariesActionsEnum.DICTIONARIES_SUPERVISION_TYPE_ITEM_CLEAR: {
      const dictionaryName = DictionaryNamesEnum.SUPERVISION_TYPE;

      return getDictionariesItemGetRequestClearState(state, dictionaryName);
    }

    case DictionariesActionsEnum.DICTIONARIES_SUPERVISION_TYPE_ITEM_GET_REQUEST: {
      const { filterUUIDs, isPlanned, federalLawUUIDs } = action.payload;
      const dictionaryName = DictionaryNamesEnum.SUPERVISION_TYPE;

      return getDictionariesFilteringItemGetRequestNewState(state, dictionaryName, [
        ...filterUUIDs,
        isPlanned.toString(),
        ...(federalLawUUIDs || ''),
      ]);
    }
    case DictionariesActionsEnum.DICTIONARIES_SUPERVISION_TYPE_ITEM_GET_SUCCESS: {
      const { data, filterUUIDs, isPlanned, federalLawUUIDs } = action.payload;
      const dictionaryName = DictionaryNamesEnum.SUPERVISION_TYPE;
      const groupKey: string = getDictionaryKey(dictionaryName, [
        ...filterUUIDs,
        isPlanned.toString(),
        ...(federalLawUUIDs || ''),
      ]);
      const newState: IDictionariesState = {
        ...state,
        items: {
          ...state.items,
          [dictionaryName]: {
            ...state.items[dictionaryName],
            [groupKey]: {
              ...state.items[dictionaryName][groupKey],
              data,
              status: DictionaryStatusesEnum.SUCCEEDED,
            },
          },
        },
      };

      return newState;
    }
    case DictionariesActionsEnum.DICTIONARIES_SUPERVISION_TYPE_ITEM_GET_FAILURE: {
      const { filterUUIDs, isPlanned, federalLawUUIDs } = action.payload;
      const dictionaryName = DictionaryNamesEnum.SUPERVISION_TYPE;

      return getDictionariesFilteringItemGetFailureNewState(state, dictionaryName, [
        ...filterUUIDs,
        isPlanned.toString(),
        ...(federalLawUUIDs || ''),
      ]);
    }

    case DictionariesActionsEnum.DICTIONARIES_ACCEPTED_DOCUMENT_ITEM_GET_REQUEST: {
      const { pair } = action.payload;
      const dictionaryName = DictionaryNamesEnum.ACCEPTED_DOCUMENT;

      return getDictionariesFilteringItemGetRequestNewState(
        state,
        dictionaryName,
        pair ? [pair?.knoVersionId, pair?.supervisionVersionId] : undefined,
      );
    }
    case DictionariesActionsEnum.DICTIONARIES_ACCEPTED_DOCUMENT_ITEM_GET_SUCCESS: {
      const { data, pair } = action.payload;
      const dictionaryName = DictionaryNamesEnum.ACCEPTED_DOCUMENT;
      const groupKey: string = getDictionaryKey(
        dictionaryName,
        pair ? [pair?.knoVersionId, pair?.supervisionVersionId] : undefined,
      );
      const newState: IDictionariesState = {
        ...state,
        items: {
          ...state.items,
          [dictionaryName]: {
            ...state.items[dictionaryName],
            [groupKey]: {
              ...state.items[dictionaryName][groupKey],
              data,
              status: DictionaryStatusesEnum.SUCCEEDED,
            },
          },
        },
      };

      return newState;
    }
    case DictionariesActionsEnum.DICTIONARIES_ACCEPTED_DOCUMENT_ITEM_GET_FAILURE: {
      const { pair } = action.payload;
      const dictionaryName = DictionaryNamesEnum.ACCEPTED_DOCUMENT;

      return getDictionariesFilteringItemGetFailureNewState(
        state,
        dictionaryName,
        pair ? [pair?.knoVersionId, pair?.supervisionVersionId] : undefined,
      );
    }

    case DictionariesActionsEnum.DICTIONARIES_CHECKLIST_ITEM_GET_REQUEST: {
      const { filterParams } = action.payload;
      const dictionaryName = DictionaryNamesEnum.CHECKLIST;

      return getDictionariesFilteringItemGetRequestNewState(state, dictionaryName, [
        filterParams.uuidKno,
        filterParams.uuidSupervision,
      ]);
    }
    case DictionariesActionsEnum.DICTIONARIES_CHECKLIST_ITEM_GET_SUCCESS: {
      const { data, filterParams } = action.payload;
      const dictionaryName = DictionaryNamesEnum.CHECKLIST;
      const groupKey: string = getDictionaryKey(dictionaryName, [filterParams.uuidKno, filterParams.uuidSupervision]);
      const newState: IDictionariesState = {
        ...state,
        items: {
          ...state.items,
          [dictionaryName]: {
            ...state.items[dictionaryName],
            [groupKey]: {
              ...state.items[dictionaryName][groupKey],
              data,
              status: DictionaryStatusesEnum.SUCCEEDED,
            },
          },
        },
      };

      return newState;
    }
    case DictionariesActionsEnum.DICTIONARIES_CHECKLIST_ITEM_GET_FAILURE: {
      const { filterParams } = action.payload;
      const dictionaryName = DictionaryNamesEnum.CHECKLIST;

      return getDictionariesFilteringItemGetFailureNewState(state, dictionaryName, [
        filterParams.uuidKno,
        filterParams.uuidSupervision,
      ]);
    }

    case DictionariesActionsEnum.ERVK_CHECK_LIST_GET_REQUEST: {
      const { filterParams } = action.payload;
      const dictionaryName = DictionaryNamesEnum.CHECKLIST;

      return getDictionariesFilteringItemGetRequestNewState(state, dictionaryName, [
        filterParams.controlKindId,
        filterParams.knmKindId,
      ]);
    }
    case DictionariesActionsEnum.ERVK_CHECK_LIST_GET_SUCCESS: {
      const { data, filterParams } = action.payload;
      const dictionaryName = DictionaryNamesEnum.ERVK_CHECK_LIST;
      const groupKey: string = getDictionaryKey(dictionaryName, [filterParams.controlKindId, filterParams.knmKindId]);
      const newState: IDictionariesState = {
        ...state,
        items: {
          ...state.items,
          [dictionaryName]: {
            ...state.items[dictionaryName],
            [groupKey]: {
              ...state.items[dictionaryName][groupKey],
              data,
              status: DictionaryStatusesEnum.SUCCEEDED,
            },
          },
        },
      };

      return newState;
    }
    case DictionariesActionsEnum.ERVK_CHECK_LIST_GET_FAILURE: {
      const { filterParams } = action.payload;
      const dictionaryName = DictionaryNamesEnum.CHECKLIST;

      return getDictionariesFilteringItemGetFailureNewState(state, dictionaryName, [
        filterParams.controlKindId,
        filterParams.knmKindId,
      ]);
    }
    case DictionariesActionsEnum.DICTIONARIES_KNM_SUBJECT_TYPES_GET_REQUEST: {
      const newState: IDictionariesState = {
        ...state,
        knmSubjectTypesStatus: DictionaryStatusesEnum.LOADING,
      };

      return newState;
    }
    case DictionariesActionsEnum.DICTIONARIES_KNM_SUBJECT_TYPES_GET_SUCCESS: {
      const { data } = action.payload;
      const newState: IDictionariesState = {
        ...state,
        knmSubjectTypes: data,
        knmSubjectTypesStatus: DictionaryStatusesEnum.SUCCEEDED,
      };

      return newState;
    }
    case DictionariesActionsEnum.DICTIONARIES_KNM_SUBJECT_TYPES_GET_FAILURE: {
      const newState: IDictionariesState = {
        ...state,
        knmSubjectTypesStatus: DictionaryStatusesEnum.FAILED,
      };

      return newState;
    }
    case DictionariesActionsEnum.DICTIONARIES_RISK_INDICATORY_ITEM_GET_REQUEST: {
      const { knoSupervisionUuid } = action.payload;
      const dictionaryName = DictionaryNamesEnum.RISK_INDICATORY;

      return getDictionariesFilteringItemGetRequestNewState(state, dictionaryName, [knoSupervisionUuid || '']);
    }
    case DictionariesActionsEnum.DICTIONARIES_RISK_INDICATORY_ITEM_GET_SUCCESS: {
      const { data, knoSupervisionUuid } = action.payload;
      const dictionaryName = DictionaryNamesEnum.RISK_INDICATORY;
      const groupKey: string = getDictionaryKey(dictionaryName, [knoSupervisionUuid || '']);
      const newState: IDictionariesState = {
        ...state,
        items: {
          ...state.items,
          [dictionaryName]: {
            ...state.items[dictionaryName],
            [groupKey]: {
              ...state.items[dictionaryName][groupKey],
              data,
              status: DictionaryStatusesEnum.SUCCEEDED,
            },
          },
        },
      };

      return newState;
    }
    case DictionariesActionsEnum.DICTIONARIES_RISK_INDICATORY_ITEM_GET_FAILURE: {
      const { knoSupervisionUuid } = action.payload;
      const dictionaryName = DictionaryNamesEnum.RISK_INDICATORY;

      return getDictionariesFilteringItemGetFailureNewState(state, dictionaryName, [knoSupervisionUuid || '']);
    }

    case DictionariesActionsEnum.DICTIONARIES_TARGET_ENTITY_FOUNDER_ITEM_GET_REQUEST: {
      const dictionaryName = DictionaryNamesEnum.TARGET_ENTITY_FOUNDER;

      return getDictionariesFilteringItemGetRequestNewState(state, dictionaryName, undefined);
    }
    case DictionariesActionsEnum.DICTIONARIES_TARGET_ENTITY_FOUNDER_ITEM_GET_SUCCESS: {
      const { data } = action.payload;
      const dictionaryName = DictionaryNamesEnum.TARGET_ENTITY_FOUNDER;
      const groupKey: string = getDictionaryKey(dictionaryName, undefined);
      const newState: IDictionariesState = {
        ...state,
        items: {
          ...state.items,
          [dictionaryName]: {
            ...state.items[dictionaryName],
            [groupKey]: {
              ...state.items[dictionaryName][groupKey],
              data,
              status: DictionaryStatusesEnum.SUCCEEDED,
            },
          },
        },
      };

      return newState;
    }
    case DictionariesActionsEnum.DICTIONARIES_TARGET_ENTITY_FOUNDER_ITEM_GET_FAILURE: {
      const dictionaryName = DictionaryNamesEnum.TARGET_ENTITY_FOUNDER;

      return getDictionariesFilteringItemGetFailureNewState(state, dictionaryName, undefined);
    }

    case DictionariesActionsEnum.DICTIONARIES_TARGET_ENTITY_ITEM_GET_REQUEST: {
      const { triplet } = action.payload;
      const dictionaryName = DictionaryNamesEnum.TARGET_ENTITY;
      // @ts-ignore
      const filter: string[] = triplet?.conditionUuid
        ? [triplet?.knoVersionId, triplet?.supervisionVersionId, triplet?.conditionUuid, triplet?.orderVersionId || '']
        : [triplet?.knoVersionId, triplet?.supervisionVersionId, triplet?.orderVersionId || ''];

      return getDictionariesFilteringItemGetRequestNewState(state, dictionaryName, triplet ? filter : undefined);
    }
    case DictionariesActionsEnum.DICTIONARIES_TARGET_ENTITY_ITEM_GET_SUCCESS: {
      const { data, triplet } = action.payload;
      const dictionaryName = DictionaryNamesEnum.TARGET_ENTITY;
      // @ts-ignore
      const filter: string[] = triplet?.conditionUuid
        ? [triplet?.knoVersionId, triplet?.supervisionVersionId, triplet?.conditionUuid, triplet?.orderVersionId || '']
        : [triplet?.knoVersionId, triplet?.supervisionVersionId, triplet?.orderVersionId || ''];
      const groupKey: string = getDictionaryKey(dictionaryName, triplet ? filter : undefined);
      const newState: IDictionariesState = {
        ...state,
        items: {
          ...state.items,
          [dictionaryName]: {
            ...state.items[dictionaryName],
            [groupKey]: {
              ...state.items[dictionaryName][groupKey],
              data,
              status: DictionaryStatusesEnum.SUCCEEDED,
            },
          },
        },
      };

      return newState;
    }
    case DictionariesActionsEnum.DICTIONARIES_TARGET_ENTITY_ITEM_GET_FAILURE: {
      const { triplet } = action.payload;
      const dictionaryName = DictionaryNamesEnum.TARGET_ENTITY;
      // @ts-ignore
      const filter: string[] = triplet?.conditionUuid
        ? [triplet?.knoVersionId, triplet?.supervisionVersionId, triplet?.conditionUuid]
        : [triplet?.knoVersionId, triplet?.supervisionVersionId];

      return getDictionariesFilteringItemGetFailureNewState(state, dictionaryName, triplet ? filter : undefined);
    }

    case DictionariesActionsEnum.DICTIONARIES_SUBJECT_TYPE_MULTI_ITEM_GET_REQUEST: {
      const { knoVersionIds, supervisionVersionIds } = action.payload;

      return getDictionariesFilteringItemGetRequestNewState(
        state,
        DictionaryNamesEnum.SUBJECT_TYPE,
        filterByKnoAndSupervision(knoVersionIds, supervisionVersionIds),
      );
    }
    case DictionariesActionsEnum.DICTIONARIES_SUBJECT_TYPE_MULTI_ITEM_GET_SUCCESS: {
      const { data, knoVersionIds, supervisionVersionIds } = action.payload;

      return getSuccessHandlerByKnoAndSupervision(
        state,
        DictionaryNamesEnum.SUBJECT_TYPE,
        data,
        knoVersionIds,
        supervisionVersionIds,
      );
    }
    case DictionariesActionsEnum.DICTIONARIES_SUBJECT_TYPE_MULTI_ITEM_GET_FAILURE: {
      const { knoVersionIds, supervisionVersionIds } = action.payload;

      return getDictionariesFilteringItemGetFailureNewState(
        state,
        DictionaryNamesEnum.SUBJECT_TYPE,
        filterByKnoAndSupervision(knoVersionIds, supervisionVersionIds),
      );
    }

    case DictionariesActionsEnum.DICTIONARIES_SUBJECT_KIND_MULTI_ITEM_GET_REQUEST: {
      const { knoVersionIds, supervisionVersionIds } = action.payload;

      return getDictionariesFilteringItemGetRequestNewState(
        state,
        DictionaryNamesEnum.SUBJECT_KIND,
        filterByKnoAndSupervision(knoVersionIds, supervisionVersionIds),
      );
    }
    case DictionariesActionsEnum.DICTIONARIES_SUBJECT_KIND_MULTI_ITEM_GET_SUCCESS: {
      const { data, knoVersionIds, supervisionVersionIds } = action.payload;

      return getSuccessHandlerByKnoAndSupervision(
        state,
        DictionaryNamesEnum.SUBJECT_KIND,
        data,
        knoVersionIds,
        supervisionVersionIds,
      );
    }
    case DictionariesActionsEnum.DICTIONARIES_SUBJECT_KIND_MULTI_ITEM_GET_FAILURE: {
      const { knoVersionIds, supervisionVersionIds } = action.payload;

      return getDictionariesFilteringItemGetFailureNewState(
        state,
        DictionaryNamesEnum.SUBJECT_KIND,
        filterByKnoAndSupervision(knoVersionIds, supervisionVersionIds),
      );
    }

    case DictionariesActionsEnum.DICTIONARIES_SUBJECT_SUB_KIND_MULTI_ITEM_GET_REQUEST: {
      const { knoVersionIds, supervisionVersionIds } = action.payload;

      return getDictionariesFilteringItemGetRequestNewState(
        state,
        DictionaryNamesEnum.SUBJECT_SUB_KIND,
        filterByKnoAndSupervision(knoVersionIds, supervisionVersionIds),
      );
    }
    case DictionariesActionsEnum.DICTIONARIES_SUBJECT_SUB_KIND_MULTI_ITEM_GET_SUCCESS: {
      const { data, knoVersionIds, supervisionVersionIds } = action.payload;

      return getSuccessHandlerByKnoAndSupervision(
        state,
        DictionaryNamesEnum.SUBJECT_SUB_KIND,
        data,
        knoVersionIds,
        supervisionVersionIds,
      );
    }
    case DictionariesActionsEnum.DICTIONARIES_SUBJECT_SUB_KIND_MULTI_ITEM_GET_FAILURE: {
      const { knoVersionIds, supervisionVersionIds } = action.payload;

      return getDictionariesFilteringItemGetFailureNewState(
        state,
        DictionaryNamesEnum.SUBJECT_SUB_KIND,
        filterByKnoAndSupervision(knoVersionIds, supervisionVersionIds),
      );
    }

    case DictionariesActionsEnum.DICTIONARIES_KNM_TYPES_ITEM_GET_REQUEST: {
      const { knoSupervisionUuid } = action.payload;
      const dictionaryName = DictionaryNamesEnum.KNM_TYPES;

      return getDictionariesFilteringItemGetRequestNewState(state, dictionaryName, [knoSupervisionUuid || '']);
    }
    case DictionariesActionsEnum.DICTIONARIES_KNM_TYPES_ITEM_GET_SUCCESS: {
      const { data, knoSupervisionUuid } = action.payload;
      const dictionaryName = DictionaryNamesEnum.KNM_TYPES;
      const groupKey: string = getDictionaryKey(dictionaryName, [knoSupervisionUuid || '']);
      const newState: IDictionariesState = {
        ...state,
        items: {
          ...state.items,
          [dictionaryName]: {
            ...state.items[dictionaryName],
            [groupKey]: {
              ...state.items[dictionaryName][groupKey],
              data,
              status: DictionaryStatusesEnum.SUCCEEDED,
            },
          },
        },
      };

      return newState;
    }
    case DictionariesActionsEnum.DICTIONARIES_KNM_TYPES_ITEM_GET_FAILURE: {
      const { knoSupervisionUuid } = action.payload;
      const dictionaryName = DictionaryNamesEnum.KNM_TYPES;

      return getDictionariesFilteringItemGetFailureNewState(state, dictionaryName, [knoSupervisionUuid || '']);
    }

    case DictionariesActionsEnum.DICTIONARIES_REQUIREMENT_ITEM_GET_REQUEST: {
      const { filterParams } = action.payload;
      const dictionaryName = DictionaryNamesEnum.REQUIREMENT;

      return getDictionariesFilteringItemGetRequestNewState(state, dictionaryName, [
        filterParams.uuidKno,
        filterParams.uuidSupervision,
      ]);
    }
    case DictionariesActionsEnum.DICTIONARIES_REQUIREMENT_ITEM_GET_SUCCESS: {
      const { data, filterParams } = action.payload;
      const dictionaryName = DictionaryNamesEnum.REQUIREMENT;
      const groupKey: string = getDictionaryKey(dictionaryName, [filterParams.uuidKno, filterParams.uuidSupervision]);
      const newState: IDictionariesState = {
        ...state,
        items: {
          ...state.items,
          [dictionaryName]: {
            ...state.items[dictionaryName],
            [groupKey]: {
              ...state.items[dictionaryName][groupKey],
              data,
              status: DictionaryStatusesEnum.SUCCEEDED,
            },
          },
        },
      };

      return newState;
    }
    case DictionariesActionsEnum.DICTIONARIES_REQUIREMENT_ITEM_GET_FAILURE: {
      const { filterParams } = action.payload;
      const dictionaryName = DictionaryNamesEnum.REQUIREMENT;

      return getDictionariesFilteringItemGetFailureNewState(state, dictionaryName, [
        filterParams.uuidKno,
        filterParams.uuidSupervision,
      ]);
    }

    case DictionariesActionsEnum.DICTIONARIES_CONDITION_TYPES_ITEM_GET_REQUEST: {
      const { uniqueUuid } = action.payload;
      const dictionaryName = DictionaryNamesEnum.CONDITION_TYPES;

      return getDictionariesFilteringItemGetRequestNewState(state, dictionaryName, [uniqueUuid || '']);
    }

    case DictionariesActionsEnum.DICTIONARIES_CONDITION_TYPES_ITEM_GET_SUCCESS: {
      const { data, uniqueUuid } = action.payload;
      const dictionaryName = DictionaryNamesEnum.CONDITION_TYPES;
      const groupKey: string = getDictionaryKey(dictionaryName, [uniqueUuid || '']);
      const newState: IDictionariesState = {
        ...state,
        items: {
          ...state.items,
          [dictionaryName]: {
            ...state.items[dictionaryName],
            [groupKey]: {
              ...state.items[dictionaryName][groupKey],
              data,
              status: DictionaryStatusesEnum.SUCCEEDED,
            },
          },
        },
      };

      return newState;
    }
    case DictionariesActionsEnum.DICTIONARIES_CONDITION_TYPES_ITEM_GET_FAILURE: {
      const { uniqueUuid } = action.payload;
      const dictionaryName = DictionaryNamesEnum.CONDITION_TYPES;

      return getDictionariesFilteringItemGetFailureNewState(state, dictionaryName, [uniqueUuid || '']);
    }

    case DictionariesActionsEnum.DICTIONARIES_APPLICATION_CONDITION_SUBJECTS_ITEM_GET_REQUEST: {
      const { conditionUuid, orderVersionUuid } = action.payload;
      const dictionaryName = DictionaryNamesEnum.APPLICATION_CONDITION_SUBJECTS;

      return getDictionariesFilteringItemGetRequestNewState(state, dictionaryName, [
        orderVersionUuid || conditionUuid || '',
      ]);
    }
    case DictionariesActionsEnum.DICTIONARIES_APPLICATION_CONDITION_SUBJECTS_ITEM_GET_SUCCESS: {
      const { data, conditionUuid, orderVersionUuid } = action.payload;
      const dictionaryName = DictionaryNamesEnum.APPLICATION_CONDITION_SUBJECTS;

      const groupKey: string = getDictionaryKey(dictionaryName, [orderVersionUuid || conditionUuid || '']);
      const newState: IDictionariesState = {
        ...state,
        items: {
          ...state.items,
          [dictionaryName]: {
            ...state.items[dictionaryName],
            [groupKey]: {
              ...state.items[dictionaryName][groupKey],
              data,
              status: DictionaryStatusesEnum.SUCCEEDED,
            },
          },
        },
      };

      return newState;
    }

    case DictionariesActionsEnum.DICTIONARIES_APPLICATION_CONDITION_SUBJECTS_ITEM_GET_FAILURE: {
      const { conditionUuid, orderVersionUuid } = action.payload;
      const dictionaryName = DictionaryNamesEnum.APPLICATION_CONDITION_SUBJECTS;

      return getDictionariesFilteringItemGetFailureNewState(state, dictionaryName, [
        orderVersionUuid || conditionUuid || '',
      ]);
    }
    case DictionariesActionsEnum.DICTIONARIES_FILTERED_FEDERAL_LAWS_GET_REQUEST: {
      return {
        ...state,
        filteredFederalLawsStatus: DictionaryStatusesEnum.LOADING,
      };
    }

    case DictionariesActionsEnum.DICTIONARIES_FILTERED_FEDERAL_LAWS_GET_SUCCESS: {
      const { data } = action.payload;

      return {
        ...state,
        filteredFederalLaws: data,
        filteredFederalLawsStatus: DictionaryStatusesEnum.SUCCEEDED,
      };
    }

    case DictionariesActionsEnum.DICTIONARIES_FILTERED_FEDERAL_LAWS_GET_FAILURE: {
      return {
        ...state,
        filteredFederalLawsStatus: DictionaryStatusesEnum.FAILED,
      };
    }

    case DictionariesActionsEnum.DICTIONARIES_SUPERVISION_TYPE_BY_SUBJECT_TYPE_ITEM_GET_REQUEST: {
      const dictionaryName = DictionaryNamesEnum.SUPERVISION_TYPE_BY_SUBJECT_TYPE;

      return getDictionariesFilteringItemGetRequestNewState(state, dictionaryName);
    }
    case DictionariesActionsEnum.DICTIONARIES_SUPERVISION_TYPE_BY_SUBJECT_TYPE_ITEM_GET_SUCCESS: {
      const { data } = action.payload;
      const groupKey: string = getDictionaryKey(DictionaryNamesEnum.SUPERVISION_TYPE_BY_SUBJECT_TYPE);

      const newState: IDictionariesState = {
        ...state,
        items: {
          ...state.items,
          [DictionaryNamesEnum.SUPERVISION_TYPE_BY_SUBJECT_TYPE]: {
            [groupKey]: {
              ...state.items[DictionaryNamesEnum.SUPERVISION_TYPE_BY_SUBJECT_TYPE][groupKey],
              data,
              status: DictionaryStatusesEnum.SUCCEEDED,
            },
          },
        },
      };

      return newState;
    }

    default:
      return state;
  }
}
